// Georgia Institute of Technology: public college page (#2596), official
// facts only.
//
// Every figure below was read on 2026-09-30 from the source it names:
//  - Georgia Tech's Common Data Set 2025-2026 (Institutional Research and
//    Planning PDF, final R4, June 3, 2026): C1 applicants/admits/enrollees
//    for Fall 2025 with the residency table, C8 test policy for Fall 2027.
//  - Georgia Tech's 2025 Fact Book (Institutional Research and Planning),
//    page 1: first-year admission by college, Fall 2025. Its totals (65,968
//    applied, 8,028 accepted) differ from the CDS; the Fact Book notes its
//    figures do not reflect the Student Right-to-Know cohort and were
//    retrieved December 16, 2025.
//  - admission.gatech.edu: First-Year Deadlines ("2026-2027 Important
//    Dates"), Standardized Tests, Major Selection.
//  - finaid.gatech.edu Undergraduate Costs: 2026-2027 first-year cost of
//    attendance, on campus, Georgia resident and out of state. The CDS G5
//    gives one transportation figure ($566); the financial aid office gives
//    $966 for out-of-state students, used here.
//  - College Scorecard API, school id 139755 (net price by family income).
// Nothing here is profile-derived, an odds figure or a student record.
// Re-check a figure against its source before editing it, and move
// `lastChecked` when you do.

export const SOURCES = {
    cds: {
        label: 'Georgia Tech Common Data Set 2025-2026',
        url: 'https://irp.gatech.edu/sites/default/files/CDS/CDS_2025-2026_FINAL_R4_03JUN2026.pdf',
        read: '2026-09-30',
    },
    factBook: {
        label: 'Georgia Tech 2025 Fact Book, Institutional Research and Planning',
        url: 'https://irp.gatech.edu/sites/default/files/FactBook/2025_FactBook_Final.pdf',
        read: '2026-09-30',
    },
    deadlines: {
        label: 'Georgia Tech Admission, First-Year Application Plans and Deadlines',
        url: 'https://admission.gatech.edu/first-year/deadlines',
        read: '2026-09-30',
    },
    tests: {
        label: 'Georgia Tech Admission, Standardized Tests',
        url: 'https://admission.gatech.edu/first-year/standardized-tests',
        read: '2026-09-30',
    },
    majors: {
        label: 'Georgia Tech Admission, Major Selection in the Application Process',
        url: 'https://admission.gatech.edu/first-year/major-selection',
        read: '2026-09-30',
    },
    costs: {
        label: 'Georgia Tech Financial Aid, Undergraduate Costs',
        url: 'https://finaid.gatech.edu/costs/undergraduate-costs',
        read: '2026-09-30',
    },
    scorecard: {
        label: 'College Scorecard, U.S. Department of Education',
        url: 'https://collegescorecard.ed.gov/school/?139755-Georgia-Institute-of-Technology-Main-Campus',
        read: '2026-10-15',
    },
};

const college = {
    slug: 'georgia-institute-of-technology',
    kbId: 'georgia_institute_of_technology',
    name: 'Georgia Institute of Technology',
    shortName: 'Georgia Tech',
    aliases: ['GT'],
    location: 'Atlanta, GA',
    control: 'Public',
    lastChecked: '2026-09-30',
    sources: SOURCES,
    // From the KB's sourced branding (Georgia Tech brand site); decoration only.
    branding: {
        primary: '#B3A369',
        secondary: '#003057',
        logo_url: 'https://storage.googleapis.com/college-counselor-assets/university-logos/georgia_institute_of_technology_logo.png',
    },

    admission: {
        entering: 'Fall 2025',
        cdsCycle: '2025-2026',
        applied: 66881,
        admitted: 8921,
        enrolled: 4071,
        source: 'cds',
        where: 'section C1',
        // As printed in the CDS: its residency admitted rows add to 8,920,
        // one short of the 8,921 total it prints.
        residency: [
            { group: 'In-state', applied: 12527, admitted: 3691 },
            { group: 'Out-of-state', applied: 44596, admitted: 4513 },
            { group: 'International', applied: 9758, admitted: 716 },
        ],
        residencyWhere: 'section C1, residency table',
    },
    // Acceptance by school or major: shown only where the school publishes it.
    byMajor: {
        year: 'Fall 2025',
        published: true,
        note: 'Georgia Tech\'s Fact Book reports first-year admission by college for fall 2025. You apply to Georgia Tech, not to a college: the college here is the one your intended major belongs to. The Fact Book\'s totals (65,968 applied, 8,028 accepted) differ from the Common Data Set\'s, and it notes its figures do not reflect the federal Student Right-to-Know cohort.',
        head: ['College', 'Applied', 'Accepted', 'Rate'],
        rows: [
            { name: 'College of Computing', applied: 13556, admitted: 1310, rate: 9.7 },
            { name: 'College of Design', applied: 2007, admitted: 313, rate: 15.6 },
            { name: 'College of Engineering', applied: 32934, admitted: 3795, rate: 11.5 },
            { name: 'College of Sciences', applied: 10419, admitted: 1541, rate: 14.8 },
            { name: 'Ivan Allen College', applied: 3200, admitted: 558, rate: 17.4 },
            { name: 'Scheller College of Business', applied: 3852, admitted: 511, rate: 13.3 },
        ],
        source: 'factBook',
        where: 'page 1, Freshman Admissions by Year and College, Fall 2025',
    },

    programs: {
        entering: 'Fall 2027',
        intro: 'You name an intended major on the Common App, and Georgia Tech looks for interest in and preparation for it. Some design majors ask for more:',
        rows: [
            { school: 'Music Technology', requirement: 'A portfolio and an essay are required with the application.' },
            { school: 'Other College of Design majors', requirement: 'A portfolio is recommended for some majors, and design faculty review it.' },
        ],
        source: 'majors',
    },

    deadlines: {
        entering: 'Fall 2027',
        headline: { label: 'Regular Decision', value: 'January 6' },
        rows: [
            { plan: 'Early Action 1 (Georgia students)', deadline: 'October 15', decision: 'Early December', binding: false },
            { plan: 'Early Action 2 (non-Georgia students)', deadline: 'November 2', decision: 'Late January', binding: false },
            { plan: 'Regular Decision', deadline: 'January 6', decision: 'Mid-March', binding: false },
        ],
        note: 'Deadlines are 11:59 pm in your time zone. Documents are due October 30, November 16 and January 22, and test scores you report yourself November 16, December 8 and January 22, plan by plan. Early Action is non-binding. To be considered for merit scholarships such as the Stamps President\'s Scholars Program, apply in an Early Action plan. The deposit is due May 1.',
        source: 'deadlines',
    },

    testing: {
        entering: 'Fall 2027',
        policy: 'Test-required',
        blind: false,
        line: 'Every first-year applicant must submit at least one SAT or ACT score. You can report it yourself in the Common App or your admission portal, and send official scores only if you enroll. AP, IB and CLT results are optional.',
        source: 'tests',
        cdsLine: 'The Common Data Set also marks the SAT or ACT as required to be considered for admission for fall 2027 applicants.',
        cdsWhere: 'section C8',
    },

    // The next cycle (2027-28, fall 2028 entry): only what the school has
    // announced, read on the date below; nothing announced says so (#2651).
    nextCycle: {
        entering: 'Fall 2028',
        checked: '2026-09-30',
        changes: [],
        source: 'deadlines',
    },

    cost: {
        year: '2026-2027',
        columns: ['In-state', 'Out-of-state'],
        rows: [
            { item: 'Tuition', amounts: [10618, 34604] },
            { item: 'Mandatory student fees', amounts: [1516, 1516] },
            { item: 'Books, course materials, supplies and equipment', amounts: [800, 800] },
            { item: 'Housing (first year, on campus)', amounts: [8318, 8318] },
            { item: 'Standard food service plan', amounts: [6310, 6310] },
            { item: 'Personal and other expenses', amounts: [2800, 2800] },
            { item: 'Transportation', amounts: [566, 966] },
        ],
        totals: [30928, 55314],
        note: 'For a first-year student living on campus, two semesters.',
        source: 'costs',
        netPrice: {
            average: 12028,
            byIncome: [
                { income: '$0 to $30,000', amount: 7687 },
                { income: '$30,001 to $48,000', amount: 7102 },
                { income: '$48,001 to $75,000', amount: 10051 },
                { income: '$75,001 to $110,000', amount: 14175 },
                { income: 'Over $110,000', amount: 18187 },
            ],
            note: 'What first-year, full-time, in-state students who received federal aid paid after grants and scholarships, by family income, in 2024-25, the latest year the Department of Education publishes. Your own price comes from Georgia Tech\'s aid offer.',
            tileDetail: 'Per year, after grants, in-state federal aid recipients',
            source: 'scorecard',
        },
    },
};

export default college;
